import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { UserContext } from "../../contexts/UserContext";
import mediaService from "../../services/mediaService";
import mediaCategoriesService, { MediaCategory } from "../../services/mediaCategoriesService";
import { X, Search, Eye, CheckCircle, FolderOpen, Image as ImageIcon, Link2, Trash2 } from "lucide-react";

export interface MediaItem {
  path: string;
  name: string;
  size?: number;
  uploadedAt?: string;
  url?: string;

  // Optional (may not exist yet in API response)
  mediaCategoryId?: string | null;
}

export type MediaChooserProps = {
  label?: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;

  /** Organization ID (defaults to userProfile?.orgId if not provided) */
  organizationId?: string;
  /** Preferred/initial category */
  categoryId?: string;
  /** If true, chooser only shows items in categoryId (and optionally uncategorized if includeUncategorized=true and allowUncategorizedWhenLimited=true) */
  limitToCategory?: boolean;
  /** If true, include an "Uncategorized" option and allow filtering to items with no category */
  includeUncategorized?: boolean;
  /** If true, show “All categories” option (default true) */
  includeAllCategoriesOption?: boolean;
  /** If true, when limitToCategory is enabled, also allow uncategorized to be included (default false = strict) */
  allowUncategorizedWhenLimited?: boolean;

  /** Inline manual URL entry (default true) */
  allowManualUrlEntry?: boolean;
  placeholder?: string;

  buttonText?: string;
  clearable?: boolean;

  /** If true, restrict to images by extension (default true) */
  imagesOnly?: boolean;

  onChange: (value: string) => void;
  onCancel?: () => void;

  /** Customize how the final value is derived from an item */
  resolveUrl?: (item: MediaItem) => string;
};

const UNCATEGORIZED = "__uncategorized__";
const ALL_CATEGORIES = "__all__";

function defaultResolveUrl(item: MediaItem): string {
  // Prefer item.url if provided
  if (item.url) {
    return mediaService.toAbsoluteUrl(item.url);
  }

  // Fallback to static /media mapping; assumes server serves /media
  // item.path might contain slashes; do not encode whole thing as one segment
  const safePath = item.path.split("/").map(encodeURIComponent).join("/");
  return mediaService.toAbsoluteUrl(`/media/${safePath}`);
}

function isLikelyImage(pathOrName: string): boolean {
  const s = pathOrName.toLowerCase();
  return (
    s.endsWith(".png") ||
    s.endsWith(".jpg") ||
    s.endsWith(".jpeg") ||
    s.endsWith(".gif") ||
    s.endsWith(".webp") ||
    s.endsWith(".svg") ||
    s.endsWith(".bmp") ||
    s.endsWith(".tif") ||
    s.endsWith(".tiff")
  );
}

export default function MediaChooser({
  label,
  value,
  required,
  disabled,

  organizationId: organizationIdProp,
  categoryId,
  limitToCategory = false,
  includeUncategorized = true,
  includeAllCategoriesOption = true,
  allowUncategorizedWhenLimited = false,

  allowManualUrlEntry = true,
  placeholder,

  buttonText = "Choose",
  clearable = true,
  imagesOnly = true,

  onChange,
  onCancel,

  resolveUrl,
}: MediaChooserProps) {
  const { userProfile } = useContext(UserContext);
  const organizationId = organizationIdProp || userProfile?.orgId;

  const [isOpen, setIsOpen] = useState(false);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [mediaCategories, setMediaCategories] = useState<MediaCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>(() => {
    if (limitToCategory && categoryId) return categoryId;
    return includeAllCategoriesOption ? ALL_CATEGORIES : "";
  });

  const [candidate, setCandidate] = useState<MediaItem | null>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFilePath, setUploadFilePath] = useState("");
  const [uploadCategory, setUploadCategory] = useState<string | undefined>(undefined);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  const searchRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const effectiveResolveUrl = resolveUrl ?? defaultResolveUrl;

  // Keep chooser list in sync with uploads/deletes without requiring refresh
  useEffect(() => {
    if (!organizationId) return;

    const unsubscribe = mediaService.subscribeMediaChanges((evt: any) => {
      if (evt.organizationId !== organizationId) return;

      if (evt.action === "added" && evt.item) {
        const newItem = evt.item as MediaItem;
        setMediaItems((prev) => {
          const key = newItem.path || newItem.url || newItem.name;
          const exists = prev.some((p) => (p.path || p.url || p.name) === key);
          if (exists) return prev;
          return [newItem, ...prev];
        });
      }

      if (evt.action === "deleted" && evt.path) {
        setMediaItems((prev) => prev.filter((p) => p.path !== evt.path));
      }
    });

    return () => unsubscribe();
  }, [organizationId]);

  // Keep category selection aligned if parent toggles limitToCategory/categoryId
  useEffect(() => {
    if (limitToCategory && categoryId) {
      setSelectedCategory(categoryId);
    }
  }, [limitToCategory, categoryId]);

  // Escape closes modal
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadMedia = async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      setError(null);

      const [files, categories] = await Promise.all([
        mediaService.getOrganizationFiles(organizationId),
        mediaCategoriesService.getCategories(organizationId).catch(() => [] as MediaCategory[]),
      ]);

      setMediaItems(files as MediaItem[]);
      setMediaCategories(categories || []);
    } catch (e) {
      console.error("MediaChooser: failed to load media", e);
      setError("Failed to load media files");
    } finally {
      setLoading(false);
    }
  };

  const open = async () => {
    if (disabled) return;
    setIsOpen(true);
    setCandidate(null);

    // Always refresh on open so newly uploaded media appears without full browser refresh
    if (organizationId) {
      await loadMedia();
    } else {
      setError("No organization id available");
    }

    // focus search next tick
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  const handleClose = () => {
    setIsOpen(false);
    setError(null);
    setSearchTerm("");
    setCandidate(null);
    onCancel?.();
  };

  const handlePreview = (item: MediaItem) => {
    const url = effectiveResolveUrl(item);
    setPreviewUrl(url);
    setIsPreviewOpen(true);
  };

  const handleSelect = (item: MediaItem) => {
    onChange(item.path);
    setIsOpen(false);
    setCandidate(null);
  };

  const handleUploadFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validation = mediaService.validateFile(file);
      if (!validation.valid) {
        setError(validation.error || 'Invalid file');
        return;
      }
      setUploadFile(file);
      setUploadFilePath(file.name);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadFilePath || !organizationId) {
      setError('Please select a file and provide a file path');
      return;
    }

    setUploadLoading(true);
    setError(null);
    setUploadSuccess(null);

    try {
      await mediaService.uploadFile(organizationId, uploadFilePath, uploadFile, uploadCategory);
      setUploadSuccess(`Successfully uploaded ${uploadFile.name}`);
      
      // Auto-select the newly uploaded file
      onChange(uploadFilePath);
      
      // Reset upload form
      setUploadFile(null);
      setUploadFilePath("");
      setUploadCategory(undefined);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      // Refresh media list
      await loadMedia();
      
      // Close modal after a brief delay
      setTimeout(() => {
        setIsOpen(false);
      }, 1000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to upload file'
      );
    } finally {
      setUploadLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    let items = mediaItems;

    if (imagesOnly) {
      items = items.filter((i) => isLikelyImage(i.name) || isLikelyImage(i.path) || (i.url ? isLikelyImage(i.url) : false));
    }

    // search
    const s = searchTerm.trim().toLowerCase();
    if (s) {
      items = items.filter(
        (i) =>
          i.name.toLowerCase().includes(s) ||
          i.path.toLowerCase().includes(s)
      );
    }

    // category filtering
    // If API doesn't provide mediaCategoryId yet, keep behavior as "no-op"
    const hasCategoryField = items.some((i) => typeof i.mediaCategoryId !== "undefined");
    if (hasCategoryField) {
      // If limitToCategory, enforce it
      if (limitToCategory && categoryId) {
        items = items.filter((i) => {
          const inCategory = (i.mediaCategoryId ?? null) === categoryId;
          const isUncat = !i.mediaCategoryId;
          if (allowUncategorizedWhenLimited && includeUncategorized) return inCategory || isUncat;
          return inCategory;
        });
      } else {
        // Otherwise use selectedCategory selection
        if (selectedCategory === ALL_CATEGORIES && includeAllCategoriesOption) {
          // keep all
        } else if (selectedCategory === UNCATEGORIZED) {
          items = items.filter((i) => !i.mediaCategoryId);
        } else if (selectedCategory && selectedCategory !== ALL_CATEGORIES) {
          items = items.filter((i) => (i.mediaCategoryId ?? null) === selectedCategory);
        }
      }
    }

    return items;
  }, [
    mediaItems,
    imagesOnly,
    searchTerm,
    selectedCategory,
    limitToCategory,
    categoryId,
    includeUncategorized,
    includeAllCategoriesOption,
    allowUncategorizedWhenLimited,
  ]);

  const thumbnailUrl = useMemo(() => {
    if (!value || !organizationId) return null;
    // If it’s already a URL, just show it
    return mediaService.getDirectUrl(organizationId, value);
  }, [value, organizationId]);

  return (
    <div className="sm:col-span-6">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label} {required ? <span className="text-red-500">*</span> : null}
        </label>
      )}

      <div className="mt-1 flex items-start gap-3">
        <div className="flex-1">
          {allowManualUrlEntry ? (
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Link2 className="w-4 h-4" />
              </div>
              <input
                type="text"
                inputMode="url"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder ?? "https://... or choose from media"}
                required={required}
                disabled={disabled}
                className="pl-9 block w-full shadow-sm sm:text-sm border border-gray-300 rounded-md py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              />
            </div>
          ) : (
            <div className="text-sm text-gray-600 border border-gray-200 rounded-md px-3 py-2 bg-white">
              {value ? <span className="break-all">{value}</span> : <span className="text-gray-400">No media selected</span>}
            </div>
          )}

          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={open}
              disabled={disabled}
              className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              <FolderOpen className="w-4 h-4" />
              {buttonText}
            </button>

            {clearable && !!value && (
              <button
                type="button"
                onClick={() => onChange("")}
                disabled={disabled}
                className="inline-flex items-center gap-2 px-3 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50"
                title="Clear"
              >
                <Trash2 className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Inline thumbnail */}
        <div className="w-20">
          {thumbnailUrl && isLikelyImage(thumbnailUrl) ? (
            <button
              type="button"
              onClick={() => {
                setPreviewUrl(thumbnailUrl);
                setIsPreviewOpen(true);
              }}
              className="block w-20 h-20 rounded-lg overflow-hidden border border-gray-200 hover:shadow-sm bg-white"
              title="Preview"
            >
              <img
                src={thumbnailUrl}
                alt="Selected media preview"
                className="w-full h-full object-cover"
              />
            </button>
          ) : (
            <div className="w-20 h-20 rounded-lg border border-gray-200 flex items-center justify-center bg-white text-gray-400">
              <ImageIcon className="w-6 h-6" />
            </div>
          )}
        </div>
      </div>

      {/* Chooser Modal */}
      {isOpen &&
        createPortal(
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
            <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Select Media</h3>
                  <p className="text-sm text-gray-500">
                    {organizationId ? "Choose from your organization’s media storage." : "No organization selected."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-2 hover:bg-gray-100 rounded-full"
                  title="Close"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Upload Section */}
              <div className="px-6 py-4 border-b border-gray-200 bg-indigo-50">
                <div className="space-y-4">
                  {/* File Input */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Select File to Upload
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleUploadFileSelect}
                      disabled={uploadLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                    />
                    {uploadFile && (
                      <p className="text-xs text-gray-600 mt-1">
                        Selected: {uploadFile.name} ({mediaService.formatFileSize(uploadFile.size)})
                      </p>
                    )}
                  </div>

                  {/* File Path */}
                  {uploadFile && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        File Path (optional to rename)
                      </label>
                      <input
                        type="text"
                        value={uploadFilePath}
                        onChange={(e) => setUploadFilePath(e.target.value)}
                        placeholder="filename.ext or folder/filename.ext"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}

                  {/* Category */}
                  {uploadFile && mediaCategories.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Category (optional)
                      </label>
                      <select
                        value={uploadCategory || ""}
                        onChange={(e) => setUploadCategory(e.target.value || undefined)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">-- No Category --</option>
                        {mediaCategories.map((category) => (
                          <option key={category.mediaCategoryId} value={category.mediaCategoryId}>
                            {category.name}
                            {category.description && ` - ${category.description}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Upload Button */}
                  {uploadFile && (
                    <button
                      type="button"
                      onClick={handleUpload}
                      disabled={uploadLoading || !uploadFile || !uploadFilePath}
                      className="w-full px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {uploadLoading ? "Uploading..." : "Upload File"}
                    </button>
                  )}

                  {uploadSuccess && (
                    <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      ✓ {uploadSuccess}
                    </div>
                  )}
                </div>
              </div>

              {/* Filters */}
              <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-3">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  {/* Category select */}
                  {mediaCategories.length > 0 && !limitToCategory && (
                    <div className="md:w-72">
                      <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-2">
                        <FolderOpen className="w-4 h-4 text-gray-400" />
                        Category
                      </label>
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {includeAllCategoriesOption && <option value={ALL_CATEGORIES}>All categories</option>}
                        {includeUncategorized && <option value={UNCATEGORIZED}>Uncategorized</option>}
                        {mediaCategories.map((c) => (
                          <option key={c.mediaCategoryId} value={c.mediaCategoryId}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Limit to category banner */}
                  {limitToCategory && categoryId && (
                    <div className="md:flex-1 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
                      Limiting to category:{" "}
                      <span className="font-semibold">
                        {mediaCategories.find((c) => c.mediaCategoryId === categoryId)?.name ?? "Selected category"}
                      </span>
                      {includeUncategorized && allowUncategorizedWhenLimited ? (
                        <span className="text-indigo-700"> (including uncategorized)</span>
                      ) : null}
                    </div>
                  )}

                  {/* Search */}
                  <div className="md:flex-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <Search className="w-4 h-4" />
                      </div>
                      <input
                        ref={searchRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by name or path..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Refresh */}
                  <div className="md:w-auto">
                    <label className="block text-xs font-medium text-transparent mb-1">.</label>
                    <button
                      type="button"
                      onClick={loadMedia}
                      disabled={loading || !organizationId}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                      {loading ? "Loading..." : "Refresh"}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                    {error}
                  </div>
                )}
              </div>

              {/* Body */}
              <div className="px-6 py-4 overflow-auto flex-1">
                {loading ? (
                  <div className="text-gray-600 text-sm">Loading media...</div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-center py-14 text-gray-500">
                    <ImageIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <div className="text-lg font-medium">No results</div>
                    <div className="text-sm">Try a different search term or category.</div>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-gray-600 mb-3">
                      Showing {filteredItems.length} of {mediaItems.length}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {filteredItems.map((item) => {
                        const url = effectiveResolveUrl(item);
                        const canThumb = isLikelyImage(item.name) || isLikelyImage(item.path) || (item.url ? isLikelyImage(item.url) : false);

                        const isActive = candidate?.path === item.path;

                        return (
                          <div
                            role="button"
                            tabIndex={0}
                            key={item.path}
                            onClick={() => setCandidate(item)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setCandidate(item);
                              }
                            }}
                            className={[
                              "text-left rounded-xl border overflow-hidden bg-white hover:shadow-sm transition-shadow cursor-pointer",
                              isActive ? "border-indigo-500 ring-2 ring-indigo-200" : "border-gray-200",
                            ].join(" ")}
                          >
                            <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                              {canThumb ? (
                                <img src={url} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="text-gray-400 flex flex-col items-center gap-2">
                                  <ImageIcon className="w-8 h-8" />
                                  <span className="text-xs">No preview</span>
                                </div>
                              )}
                            </div>

                            <div className="p-3">
                              <div className="text-sm font-medium text-gray-800 truncate" title={item.name}>
                                {item.name}
                              </div>
                              <div className="text-xs text-gray-500 truncate" title={item.path}>
                                {item.path}
                              </div>

                              <div className="mt-3 flex items-center justify-between gap-2">
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePreview(item);
                                    }}
                                    className="p-2 rounded-lg hover:bg-purple-50 text-purple-700"
                                    title="Preview"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </div>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelect(item);
                                  }}
                                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
                                  title="Select"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Select
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {candidate ? (
                    <>
                      Selected: <span className="font-medium text-gray-800">{candidate.name}</span>
                    </>
                  ) : (
                    "Click an item to highlight it, or use Select on any tile."
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!candidate}
                    onClick={() => candidate && handleSelect(candidate)}
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Select highlighted
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Preview Modal */}
      {isPreviewOpen && previewUrl &&
        createPortal(
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[999999] flex items-center justify-center p-4">
            <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl relative max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  setIsPreviewOpen(false);
                  setPreviewUrl(null);
                }}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100/80 rounded-full transition-colors z-10"
                title="Close"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>

              <div className="p-4 overflow-auto flex-1 flex items-start justify-center">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-w-full object-contain rounded-lg"
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
